import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebounce } from './useDebounce';
import { SEARCH_DEBOUNCE_DELAY } from '@/constants';
import { logger } from '../utils/logger';
import { tasksService } from '@/services/api/tasksService';
import {
  mapTaskListItemFromResponse,
  mapTaskResponseToListItem,
} from '@/store/mappers/taskMapper';
import type { TaskListItem } from '@/types';

/** Maximum number of tasks to fetch per search request */
const TASK_SEARCH_PAGE_SIZE = 20;

interface UseTaskSearchReturn {
  tasks: TaskListItem[];
  isLoading: boolean;
  onSearchChange: (query: string) => void;
}

/**
 * Server-side task search hook with debounced input.
 * Fetches tasks from the API and ensures the selected task is always present in results.
 *
 * @param selectedTaskId - Currently selected task ID to ensure it appears in results
 */
export function useTaskSearch(
  selectedTaskId: string | null
): UseTaskSearchReturn {
  const [tasks, setTasks] = useState<TaskListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const fetchIdRef = useRef(0);
  const fetchedSelectedIdRef = useRef<string | null>(null);

  const { debouncedValue: debouncedSearch } = useDebounce(
    searchValue,
    SEARCH_DEBOUNCE_DELAY
  );

  // Fetch tasks when debounced search value changes
  useEffect(() => {
    const currentFetchId = ++fetchIdRef.current;

    const fetchTasks = async () => {
      setIsLoading(true);
      try {
        const response = await tasksService.getAll({
          search: debouncedSearch || undefined,
          pageSize: TASK_SEARCH_PAGE_SIZE,
        });

        // Prevent stale responses from overwriting newer results
        if (currentFetchId !== fetchIdRef.current) return;

        const mappedTasks = response.data.items.map(
          mapTaskListItemFromResponse
        );
        setTasks(mappedTasks);
      } catch (error: unknown) {
        if (currentFetchId !== fetchIdRef.current) return;
        logger.error('Failed to fetch tasks for search', 'useTaskSearch', {
          error,
        });
        setTasks([]);
      } finally {
        if (currentFetchId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchTasks();
  }, [debouncedSearch]);

  // Ensure selected task is present in results
  useEffect(() => {
    if (!selectedTaskId) {
      fetchedSelectedIdRef.current = null;
      return;
    }

    if (fetchedSelectedIdRef.current === selectedTaskId) return;
    fetchedSelectedIdRef.current = selectedTaskId;

    const fetchSelectedTask = async () => {
      try {
        const response = await tasksService.getById(selectedTaskId);
        const selectedItem = mapTaskResponseToListItem(response.data);

        setTasks((currentTasks) => {
          const stillMissing = !currentTasks.some(
            (task) => task.id === selectedTaskId
          );
          return stillMissing ? [selectedItem, ...currentTasks] : currentTasks;
        });
      } catch (error: unknown) {
        logger.error('Failed to fetch selected task', 'useTaskSearch', {
          error,
        });
      }
    };

    fetchSelectedTask();
  }, [selectedTaskId]);

  const onSearchChange = useCallback((query: string) => {
    setSearchValue(query);
  }, []);

  return { tasks, isLoading, onSearchChange };
}
